import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Checkbox } from 'antd';
import { Button, Modal } from 'components/gyramais';
import { AllBadesulHighlights, AllTermsOfUseBadesul } from 'utils';
import './styles.less';

/**
 * Destaques da linha Badesul + termo integral + ciência do parceiro (GYR-1711/GYR-1712).
 *
 * Consumido pelas DUAS portas do parceiro para criar uma operação Badesul:
 * `NewClientScreen` (cliente novo) e `NewLoanScreen` (cliente já existente).
 *
 * Só as carteiras com termo escrito em `src/utils/badesul-terms.js` renderizam alguma coisa —
 * as demais (Microcrédito, Reconstrução RS, Enquadramento) seguem sem bloco e sem ciência,
 * como era antes. Por isso o componente devolve `null` quando não há conteúdo: quem chama não
 * precisa duplicar essa decisão.
 */
// Lookup por chave própria: `AllBadesulHighlights['constructor']` resolveria pela cadeia de
// protótipos, passaria pelo guard de "tem conteúdo" e quebraria o `.map` logo abaixo. Hoje o
// `portfolioName` só vem de um `<Select>` fechado, mas a mesma função decide o que renderiza e
// o que o guard do `handleNext` exige — as duas leituras precisam ser a mesma.
const byPortfolio = (map, portfolioName) => (
  Object.prototype.hasOwnProperty.call(map, portfolioName) ? map[portfolioName] : undefined
);

const BadesulLineTerms = ({ portfolioName, acknowledged = false, onAcknowledgedChange }) => {
  const [termsModalVisible, setTermsModalVisible] = useState(false);
  const closeTermsModal = () => setTermsModalVisible(false);

  const highlights = byPortfolio(AllBadesulHighlights, portfolioName);
  const terms = byPortfolio(AllTermsOfUseBadesul, portfolioName);

  // Basta ter destaques OU termo escrito. Chavear só em `highlights` faria uma carteira com
  // termo e sem destaques sumir da tela em silêncio, sem exigir ciência e sem erro nenhum.
  if (!highlights && !terms) {
    return null;
  }

  return (
    <>
      <section className="badesul-line-terms" aria-label={`Regras da linha ${portfolioName}`}>
        {(highlights || []).map(({ title, items }) => (
          <div className="badesul-line-terms-group" key={title}>
            <h6 className="badesul-line-terms-title">{title}</h6>
            <ul>
              {items.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </div>
        ))}

        {terms && (
          <Button
            link
            id="badesul-terms-button"
            onClick={() => setTermsModalVisible(true)}
          >
            Ver termo completo
          </Button>
        )}

        <Checkbox
          id="badesul-term-acknowledge-checkbox"
          checked={acknowledged}
          onChange={({ target: { checked } }) => onAcknowledgedChange(checked)}
        >
          Li e informei ao cliente as regras da linha
          {' '}
          {portfolioName}
          .
        </Checkbox>
      </section>

      {terms && (
        <Modal
          title={`Termo de Ciência — Linha ${portfolioName}`}
          visible={termsModalVisible}
          onClose={closeTermsModal}
          okButton={{
            text: 'Fechar',
            onClick: closeTermsModal,
          }}
        >
          <div className="badesul-line-terms-container">
            {/* o <pre> é o container de scroll: precisa ser focável para quem
                navega só por teclado conseguir ler o termo até o fim (WCAG 2.1.1) */}
            {/* eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex */}
            <pre tabIndex={0} role="region" aria-label="Texto do termo">
              {terms}
            </pre>
          </div>
        </Modal>
      )}
    </>
  );
};

/**
 * A tela só precisa exigir ciência quando o componente de fato renderiza o checkbox —
 * exportado junto para que o guard do `handleNext` não derive essa regra por conta própria
 * e acabe divergindo do que está na tela.
 */
export const requiresBadesulAcknowledgement = (portfolioName) => Boolean(
  byPortfolio(AllBadesulHighlights, portfolioName)
  || byPortfolio(AllTermsOfUseBadesul, portfolioName),
);

BadesulLineTerms.propTypes = {
  portfolioName: PropTypes.string,
  acknowledged: PropTypes.bool,
  onAcknowledgedChange: PropTypes.func.isRequired,
};

export default BadesulLineTerms;
